import type { BatchQuery, TransactionQuery } from '@/api/inventory.api';
import type { BrandQuery, MedicineSearchQuery } from '@/api/catalog.api';
import type { ListingQuery } from '@/api/listings.api';
import type { PharmacyQuery } from '@/api/pharmacies.api';
import type { PageQuery } from './api-client';
import type { Uuid } from '@/api/types';

/**
 * One place every cache key is spelled, so an invalidation after a write cannot
 * miss a list because a page typed its key slightly differently.
 *
 * Pharmacy-scoped keys carry the pharmacy id in the *prefix*, not the filter
 * object — switching the scoped pharmacy must not show another pharmacy's stock
 * for the frame before the refetch lands.
 */
export const queryKeys = {
  me: ['auth', 'me'] as const,

  catalog: {
    all: ['catalog'] as const,
    medicines: (query: MedicineSearchQuery) => ['catalog', 'medicines', query] as const,
    medicine: (id: Uuid) => ['catalog', 'medicine', id] as const,
    regulatoryProfile: (id: Uuid) => ['catalog', 'medicine', id, 'regulatory-profile'] as const,
    aliases: (id: Uuid) => ['catalog', 'medicine', id, 'aliases'] as const,
    images: (id: Uuid) => ['catalog', 'medicine', id, 'images'] as const,
    substitutions: (id: Uuid) => ['catalog', 'medicine', id, 'substitutions'] as const,
    categories: ['catalog', 'categories'] as const,
    manufacturers: (query: PageQuery) => ['catalog', 'manufacturers', query] as const,
    brands: (query: BrandQuery) => ['catalog', 'brands', query] as const,
    /** Prefix for invalidating every brand list, whatever its filters. */
    brandsAll: ['catalog', 'brands'] as const,
    recalls: (query: Record<string, unknown>) => ['catalog', 'recalls', query] as const,
    recallAffected: (recallId: string) => ['catalog', 'recalls', recallId, 'affected'] as const,
  },

  orders: {
    all: ['orders'] as const,
    list: (query: Record<string, unknown>) => ['orders', 'list', query] as const,
    facets: ['orders', 'facets'] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
  },

  deliveries: {
    all: ['deliveries'] as const,
    list: (query: Record<string, unknown>) => ['deliveries', 'list', query] as const,
    facets: (riderId?: string) => ['deliveries', 'facets', riderId ?? 'all'] as const,
    detail: (id: string) => ['deliveries', 'detail', id] as const,
    riders: ['deliveries', 'riders'] as const,
    riderLocation: (riderId: string) => ['deliveries', 'riders', riderId, 'location'] as const,
    riderCash: (riderId: string) => ['deliveries', 'riders', riderId, 'cash'] as const,
    cashLedger: (query: Record<string, unknown>) => ['deliveries', 'cash-ledger', query] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (query: Record<string, unknown>) => ['audit', 'list', query] as const,
    facets: ['audit', 'facets'] as const,
    history: (entityType: string, entityId: string) =>
      ['audit', 'history', entityType, entityId] as const,
    config: ['audit', 'config'] as const,
  },

  users: {
    all: ['users'] as const,
    list: (query: Record<string, unknown>) => ['users', 'list', query] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    summary: ['users', 'summary'] as const,
  },

  pharmacies: {
    hours: (pharmacyId: string) => ['pharmacies', pharmacyId, 'hours'] as const,
    openNow: (pharmacyId: string) => ['pharmacies', pharmacyId, 'open-now'] as const,
    closures: (pharmacyId: string) => ['pharmacies', pharmacyId, 'closures'] as const,
    metrics: (pharmacyId: string, days: number) =>
      ['pharmacies', pharmacyId, 'metrics', days] as const,
    all: ['pharmacies'] as const,
    list: (query: PharmacyQuery) => ['pharmacies', 'list', query] as const,
    detail: (id: Uuid) => ['pharmacies', 'detail', id] as const,
    licenses: (id: Uuid) => ['pharmacies', 'detail', id, 'licenses'] as const,
    staff: (id: Uuid) => ['pharmacies', 'detail', id, 'staff'] as const,
  },

  coupons: {
    all: ['coupons'] as const,
    list: (query: Record<string, unknown>) => ['coupons', 'list', query] as const,
    detail: (id: Uuid) => ['coupons', 'detail', id] as const,
  },

  /** System settings. SUPER_ADMIN only — see api/settings.api.ts. */
  settings: {
    messaging: ['settings', 'messaging'] as const,
    authLimits: ['settings', 'auth-limits'] as const,
  },

  fees: {
    schedule: ['fees', 'schedule'] as const,
    /** The preview is keyed by the DRAFT being edited, not by what is saved. */
    preview: (config: unknown, distances: number[]) =>
      ['fees', 'preview', config, distances] as const,
  },

  /* Keyed by pharmacy now the book is one: changing the scoped shop must not
     serve the previous shop's suppliers for the frame before the refetch. */
  suppliers: (pharmacyId: Uuid, query: Record<string, unknown>) =>
    ['suppliers', pharmacyId, query] as const,

  inventory: {
    stockHealth: (query: { withinDays: number; lowStockThreshold: number }) =>
      ['inventory', 'stock-health', query] as const,
    medicineStock: (medicineId: Uuid) => ['inventory', 'medicine-stock', medicineId] as const,
    scope: (pharmacyId: Uuid) => ['inventory', pharmacyId] as const,
    balances: (pharmacyId: Uuid, query: PageQuery) =>
      ['inventory', pharmacyId, 'balances', query] as const,
    batches: (pharmacyId: Uuid, query: BatchQuery) =>
      ['inventory', pharmacyId, 'batches', query] as const,
    transactions: (pharmacyId: Uuid, query: TransactionQuery) =>
      ['inventory', pharmacyId, 'transactions', query] as const,
    expiring: (pharmacyId: Uuid, withinDays: number) =>
      ['inventory', pharmacyId, 'expiring', withinDays] as const,
    lowStock: (pharmacyId: Uuid, threshold: number) =>
      ['inventory', pharmacyId, 'low-stock', threshold] as const,
  },

  settlements: {
    all: ['settlements'] as const,
    list: (query: Record<string, unknown>) => ['settlements', 'list', query] as const,
    facets: ['settlements', 'facets'] as const,
    detail: (id: Uuid) => ['settlements', id] as const,
    items: (id: Uuid) => ['settlements', id, 'items'] as const,
  },

  prescriptions: {
    all: ['prescriptions'] as const,
    list: (query: PageQuery) => ['prescriptions', 'list', query] as const,
    facets: ['prescriptions', 'facets'] as const,
    detail: (id: Uuid) => ['prescriptions', id] as const,
  },

  listings: {
    scope: (pharmacyId: Uuid) => ['listings', pharmacyId] as const,
    list: (pharmacyId: Uuid, query: ListingQuery) =>
      ['listings', pharmacyId, 'list', query] as const,
    unlisted: (pharmacyId: Uuid) => ['listings', pharmacyId, 'unlisted'] as const,
    unsellable: (pharmacyId: Uuid) => ['listings', pharmacyId, 'unsellable'] as const,
    supplyRules: (pharmacyId: Uuid) => ['listings', pharmacyId, 'supply-rules'] as const,
    /* Platform-wide, so deliberately outside the pharmacy-scoped prefix. */
    retailRules: () => ['retail-rules'] as const,
  },
} as const;
